import { useEffect, useState } from "react";
import { CalendarCheck, ImageUp, Sparkles, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CharacterPicture = ({ hasImage, version }) => {
  const [src, setSrc] = useState(null);
  const [ver, setVer] = useState(version);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!hasImage && !ver) return;
    let url;
    api.get("/work-extras/character-image", { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [hasImage, ver]);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/work-extras/character-image", fd);
      setVer(Date.now());
      toast.success("Character picture saved.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't upload. Try again."); }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <div className="mt-4" data-testid="character-picture">
      {src ? <img src={src} alt="Your AI character" className="w-40 h-40 object-cover rounded-lg border border-[#2A2E39]" data-testid="character-picture-img" />
        : <p className="text-xs text-gray-400">No picture yet. The first character image your coach makes saves here, or upload your own.</p>}
      <label className="mt-2 inline-flex items-center gap-2 border border-[#2A2E39] hover:border-white px-3 py-1.5 rounded text-xs font-bold cursor-pointer" data-testid="character-picture-upload">
        <ImageUp size={14} /> {busy ? "Uploading..." : src ? "Swap picture" : "Upload a picture"}
        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} disabled={busy} data-testid="character-picture-input" />
      </label>
      <p className="mt-1 text-xs text-gray-400">Your coach uses this picture so new images keep the same face. You can also swap it from any coach image with "Use as my character picture".</p>
    </div>
  );
};

const CharacterCard = ({ text, hasImage, imageAt }) => {
  const [val, setVal] = useState(text);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.put("/work-extras/character", { text: val }); toast.success("Character saved. Your coach uses it in every prompt."); }
    catch { toast.error("Couldn't save. Try again."); }
    setSaving(false);
  };
  return (
    <div className="card-panel p-5 mt-6" data-testid="character-card">
      <div className="eyebrow text-[#FD0F10] flex items-center gap-2"><UserRound size={14} /> Your AI character</div>
      <p className="text-sm text-gray-300 mt-2">Your coach puts this exact description into every image and video prompt, so your character always looks and sounds the same.</p>
      <textarea value={val} onChange={(e) => setVal(e.target.value)} rows={5} placeholder="Name, age, look, outfit, voice, personality and niche. Or ask your coach to write it."
        className="mt-3 w-full bg-[#0B0C0E] border border-[#2A2E39] rounded p-3 text-sm text-gray-100" data-testid="character-card-input" />
      <button onClick={save} disabled={saving || !val.trim()} className="mt-2 hero-accent px-4 py-2 rounded font-bold text-sm disabled:opacity-60" data-testid="character-card-save">
        {saving ? "Saving..." : "Save character"}
      </button>
      <CharacterPicture hasImage={hasImage} version={imageAt} />
    </div>
  );
};

const ServiceTip = ({ tip, onDismiss }) => (
  <div className="card-panel p-5 mt-6 relative" data-testid="service-tip">
    <button onClick={onDismiss} className="absolute top-3 right-3 text-gray-400 hover:text-white" aria-label="Dismiss" data-testid="service-tip-dismiss"><X size={16} /></button>
    <div className="eyebrow text-[#FD0F10] flex items-center gap-2"><Sparkles size={14} /> Offer them this next</div>
    <p className="font-display font-bold text-lg text-white mt-1" data-testid="service-tip-offer">{tip.offer}</p>
    <p className="text-sm text-gray-300 mt-1" data-testid="service-tip-text">{tip.tip}</p>
  </div>
);

const CheckIn = ({ c, onDismiss }) => (
  <div className="card-panel p-5 mt-6 relative" data-testid={`checkin-${c.id}`}>
    <button onClick={onDismiss} className="absolute top-3 right-3 text-gray-400 hover:text-white" aria-label="Dismiss" data-testid={`checkin-dismiss-${c.id}`}><X size={16} /></button>
    <div className="eyebrow text-[#FD0F10] flex items-center gap-2"><CalendarCheck size={14} /> Time to check in</div>
    <p className="text-sm text-gray-200 mt-1">It's been about a month since you finished this job: <span className="text-white font-bold">{c.job}</span>. Check in with the customer to see how the work is holding up, and offer them <span className="text-white font-bold">{c.offer}</span>. Your coach can write the message for you.</p>
  </div>
);

export const WorkExtras = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/work-extras").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const dismiss = () => { api.post("/work-extras/tip/dismiss").catch(() => {}); setD({ ...d, service_tip: null }); };
  const dismissCheckin = (id) => { api.post(`/work-extras/checkin/${id}/dismiss`).catch(() => {}); setD({ ...d, checkins: d.checkins.filter((c) => c.id !== id) }); };
  return (
    <>
      {(d.checkins || []).map((c) => <CheckIn key={c.id} c={c} onDismiss={() => dismissCheckin(c.id)} />)}
      {d.service_tip && <ServiceTip tip={d.service_tip} onDismiss={dismiss} />}
      {d.show_character && <CharacterCard text={d.character} hasImage={d.has_image} imageAt={d.image_at} />}
    </>
  );
};
